<template>
    <div class="tw:w-full">
        <label
            v-if="label"
            :for="inputId"
            class="tw:block tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-2"
        >
            {{ label }}
            <span v-if="hint" class="tw:text-xs tw:font-normal tw:text-gray-500">
                {{ hint }}
            </span>
        </label>

        <input
            :id="inputId"
            ref="fileInputRef"
            type="file"
            :accept="accept"
            :class="inputClass"
            :disabled="disabled"
            :data-cy="dataCy"
            @change="onChange"
        />

        <p
            v-if="error"
            class="tw:text-sm tw:text-red-600 tw:mt-2"
            :data-cy="errorDataCy"
        >
            {{ error }}
        </p>
    </div>
</template>

<script setup>
    import { ref, computed } from 'vue'

    /**
     * FileInput — Reusable styled file picker for the admin UI.
     *
     * Wraps a native `<input type="file">` with the project's visual
     * conventions: an associated label, a clear hint line, and an
     * optional inline error message. The parent component owns the
     * upload logic (this is purely a presentation primitive — it does
     * not perform any HTTP request).
     *
     * @prop {String}  label           Visible label rendered above the input.
     * @prop {String}  hint            Sub-label hint (e.g. "(.xlsx, máx. 5 MB)").
     * @prop {String}  accept          Comma-separated accepted file types
     *                                  (e.g. ".xlsx,application/vnd...").
     * @prop {String}  dataCy          data-cy attribute on the input.
     * @prop {String}  error           Error message rendered below the input.
     * @prop {String}  errorDataCy     data-cy attribute for the error line.
     * @prop {Boolean} disabled        Disable the input.
     *
     * @emits file-selected — (file: File | null) When the user picks (or clears) a file.
     */
    const props = defineProps({
        label: { type: String, default: '' },
        hint: { type: String, default: '' },
        accept: { type: String, default: '' },
        dataCy: { type: String, default: '' },
        error: { type: String, default: '' },
        errorDataCy: { type: String, default: '' },
        disabled: { type: Boolean, default: false }
    })

    const emit = defineEmits(['file-selected'])

    const fileInputRef = ref(null)

    // The native input id is derived from the data-cy so that the label
    // `for` attribute is always valid and unique within the modal.
    const inputId = computed(() => props.dataCy || 'file-input')

    const inputClass = computed(() => [
        'tw:block tw:w-full tw:text-sm tw:text-gray-700 tw:pl-0',
        'tw:file:mr-4 tw:file:px-3 tw:file:py-2',
        'tw:file:rounded tw:file:border-0 tw:file:text-sm tw:file:font-semibold',
        'tw:file:bg-blue-50 tw:file:text-blue-700 tw:hover:file:bg-blue-100',
        props.disabled ? 'tw:opacity-50 tw:cursor-not-allowed' : ''
    ])

    function onChange(event) {
        const file = event.target.files && event.target.files[0]
        emit('file-selected', file || null)
    }

    /**
     * Public API: lets the parent reset the native input value (e.g. when
     * the user reopens the modal or retries after an error). Equivalent
     * to `e.target.value = ''` on the `<input>`.
     */
    function reset() {
        if (fileInputRef.value) {
            fileInputRef.value.value = ''
        }
    }

    defineExpose({ reset })
</script>
